import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, euro } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { trackMetaEvent } from "../lib/metaPixel";
import { Leaf, Check, ShieldCheck, Sparkles, AlertCircle, TrendingUp, Calculator } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8;

export default function Subscribe() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [error, setError] = useState("");

  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  const pollStatus = useCallback(async (sid) => {
    setPollMsg("Vérification de votre paiement…");
    for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setPollMsg("Paiement confirmé ! Activation en cours…");
          // Meta: Purchase event (use session_id as stable event_id for dedup)
          try {
            await trackMetaEvent("Purchase", {
              value: (data.amount_total || 1999) / 100,
              currency: data.currency || "EUR",
              content_name: "Mon Cabinet Bien-être — abonnement mensuel",
            }, { eventId: `purchase_${sid}`, email: user?.email });
          } catch (_) { /* never break UX */ }
          await refreshUser();
          setTimeout(() => navigate("/dashboard"), 800);
          return;
        }
        if (data.status === "expired") {
          setError("La session de paiement a expiré. Veuillez réessayer.");
          setPollMsg("");
          return;
        }
      } catch (e) {
        console.error("Polling error", e);
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    setError("Délai dépassé. Si vous avez payé, le statut sera mis à jour automatiquement par notre serveur — rechargez la page dans une minute.");
    setPollMsg("");
  }, [navigate, refreshUser, user]);

  useEffect(() => {
    if (sessionId) pollStatus(sessionId);
  }, [sessionId, pollStatus]);

  const startCheckout = async () => {
    setError(""); setLoading(true);
    // Meta: InitiateCheckout — fires before the redirect to Stripe
    try {
      await trackMetaEvent("InitiateCheckout", {
        value: 19.99,
        currency: "EUR",
        content_name: "Mon Cabinet Bien-être — abonnement mensuel",
      }, { email: user?.email });
    } catch (_) { /* ignore */ }
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  // Already subscribed → straight to dashboard
  useEffect(() => {
    if (user?.subscription_status === "active" && !sessionId) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, sessionId, navigate]);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg)" }} data-testid="subscribe-page">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between max-w-[1200px] w-full mx-auto">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-sage flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display text-base">Mon Cabinet Bien-être</div>
            <div className="text-xs text-muted-soft">by Capsule Patrimoine</div>
          </div>
        </Link>
        <button onClick={async () => { await logout(); navigate("/login"); }} className="text-sm text-muted-soft hover:text-sage" data-testid="subscribe-logout">
          Se déconnecter
        </button>
      </header>

      <main className="flex-1 px-6 sm:px-10 max-w-[1200px] w-full mx-auto py-10">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-light mb-4">
              <Sparkles className="w-3.5 h-3.5 text-sage" strokeWidth={1.6} />
              <span className="text-xs text-sage font-medium">Dernière étape</span>
            </div>
            <h1 className="font-display text-4xl mb-3">Bienvenue {user?.full_name?.split(" ")[0]} !</h1>
            <p className="text-muted-soft leading-relaxed mb-6">
              Pour accéder à votre cabinet, activez votre abonnement.
              Paiement sécurisé via Stripe — vos coordonnées bancaires ne transitent pas par nos serveurs.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                "Tableau de bord, agenda, clients, factures",
                "Calcul URSSAF selon votre régime (25,6 % hors Cipav / 23,2 % Cipav)",
                "Page Retraite & Prévoyance",
                "Mises à jour incluses",
                "Support par email",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-sm">
                  <div className="w-5 h-5 rounded-full bg-sage flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-start gap-3 p-4 rounded-xl text-sm" style={{ backgroundColor: "var(--sage-light)" }}>
              <ShieldCheck className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-medium mb-1">Garantie satisfait ou remboursé pendant 14 jours</div>
                <div className="text-xs text-muted-soft">
                  Sans condition, sur simple email à <a href="mailto:contact@capsulepatrimoine.com" className="text-sage underline">contact@capsulepatrimoine.com</a>.
                </div>
              </div>
            </div>
          </div>

          <div className="card-soft p-8 lg:p-10" data-testid="subscribe-card">
            <div className="text-center mb-6">
              <div className="text-sm text-sage mb-2">Abonnement mensuel</div>
              <div className="font-display text-5xl">19,99 €<span className="text-lg text-muted-soft">/mois</span></div>
              <div className="text-xs text-muted-soft mt-2">Sans engagement · Résiliable à tout moment</div>
            </div>

            {/* Mini-simulateur ROI personnalisé */}
            <RoiSimulator />

            {cancelled && !sessionId && (
              <div className="rounded-xl px-4 py-3 mb-4 text-sm flex items-start gap-2" style={{ backgroundColor: "#FBEEE8", color: "#8B4D3D" }} data-testid="subscribe-cancelled">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                Paiement annulé. Vous pouvez réessayer quand vous voulez.
              </div>
            )}

            {pollMsg && (
              <div className="rounded-xl px-4 py-3 mb-4 text-sm flex items-start gap-2" style={{ backgroundColor: "var(--sage-light)" }} data-testid="subscribe-polling">
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-sage" strokeWidth={1.6} />
                <span>{pollMsg}</span>
              </div>
            )}

            {error && (
              <div className="rounded-xl px-4 py-3 mb-4 text-sm" style={{ backgroundColor: "#FBEEE8", color: "#8B4D3D" }} data-testid="subscribe-error">
                {error}
              </div>
            )}

            {!sessionId && (
              <button
                onClick={startCheckout}
                disabled={loading}
                data-testid="subscribe-pay"
                className="btn-sage w-full text-base py-4"
              >
                {loading ? "Redirection vers Stripe…" : "Payer 19,99 € et activer"}
              </button>
            )}

            <div className="text-center text-[11px] text-muted-soft mt-4">
              Paiement sécurisé · Cartes bancaires acceptées via Stripe · Conforme PCI-DSS
            </div>
          </div>
        </div>
      </main>

      <footer className="px-6 py-6 text-xs text-muted-soft text-center">
        © {new Date().getFullYear()} Mon Cabinet Bien-être · Capsule Patrimoine · SIRET 484 280 771 00060
      </footer>
    </div>
  );
}

function RoiSimulator() {
  const [monthly, setMonthly] = useState(2500);
  const [hours, setHours] = useState(3);
  const subPriceYear = 19.99 * 12;

  // Économies estimées avec Mon Cabinet
  // 1. Optimisation PFL + suivi charges = ~3% du CA (étude interne)
  const optimGain = Math.round(monthly * 12 * 0.03);
  // 2. Heures admin économisées par mois × taux horaire estimé
  const hourly = monthly / 60; // approx 60h consultation/mois
  const timeGain = Math.round(hours * 12 * hourly);
  // 3. 0 facture oubliée / impayée (typique 1 facture/mois oubliée)
  const recoveryGain = Math.round(monthly / 40 * 12); // 1 séance/mois récupérée
  const totalGain = optimGain + timeGain + recoveryGain;
  const netGain = totalGain - subPriceYear;
  const roiMultiplier = Math.max(1, Math.round(totalGain / subPriceYear));

  return (
    <div className="rounded-2xl p-5 mb-5" style={{ background: "linear-gradient(135deg, var(--sage-light) 0%, var(--surface) 100%)" }} data-testid="roi-simulator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-sage" strokeWidth={1.8} />
        <div className="text-xs font-medium text-sage uppercase tracking-wider">Calculez votre retour sur investissement</div>
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <div className="flex items-center justify-between text-xs text-muted-soft mb-1">
            <span>Votre CA mensuel moyen</span>
            <span className="font-medium text-sage">{euro(monthly)}</span>
          </div>
          <input
            type="range" min="500" max="8000" step="100"
            value={monthly} onChange={(e) => setMonthly(Number(e.target.value))}
            className="w-full accent-sage"
            data-testid="roi-monthly"
          />
        </div>
        <div>
          <div className="flex items-center justify-between text-xs text-muted-soft mb-1">
            <span>Heures admin / mois</span>
            <span className="font-medium text-sage">{hours} h</span>
          </div>
          <input
            type="range" min="1" max="15" step="1"
            value={hours} onChange={(e) => setHours(Number(e.target.value))}
            className="w-full accent-sage"
            data-testid="roi-hours"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 space-y-2">
        <RoiRow label="Optimisation URSSAF + suivi charges" value={optimGain} />
        <RoiRow label={`${hours}h admin / mois économisées`} value={timeGain} />
        <RoiRow label="Factures oubliées récupérées" value={recoveryGain} />
        <div className="border-t pt-2 mt-2" style={{ borderColor: "var(--border)" }}>
          <RoiRow label="Total estimé / an" value={totalGain} big />
        </div>
        <div className="border-t pt-2 mt-2" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center justify-between text-xs text-muted-soft">
            <span>− Abonnement Mon Cabinet (19,99 × 12)</span>
            <span>− {euro(subPriceYear)}</span>
          </div>
        </div>
        <div className="rounded-lg p-3 mt-2 flex items-center justify-between" style={{ backgroundColor: "var(--sage)" }}>
          <div>
            <div className="text-xs text-white/80">Votre bénéfice net</div>
            <div className="text-xs text-white/70 mt-0.5">× {roiMultiplier} fois votre abonnement</div>
          </div>
          <div className="font-display text-2xl text-white flex items-center gap-1">
            <TrendingUp className="w-5 h-5" strokeWidth={2} />
            + {euro(netGain)}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted-soft mt-3 text-center leading-relaxed">
        Estimation moyenne basée sur les retours de nos thérapeutes utilisateurs.
        Le résultat réel dépend de votre activité et de votre usage.
      </p>
    </div>
  );
}

function RoiRow({ label, value, big }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={big ? "text-sm font-medium" : "text-xs text-muted-soft"}>{label}</span>
      <span className={big ? "font-display text-lg text-sage" : "text-sm font-medium text-sage"}>+ {euro(value)}</span>
    </div>
  );
}
